import { ProjectCardForm } from '../../components/ProjectCardForm/ProjectCardForm';
import {
  exceedsTotalEstimatedCosts,
  ExpensesTotal,
  fillMissingExpenseYears,
  getDefaultYearlyExpensesDataMap,
  getProjectCardValues,
  getYearlyExpensesDataMap,
  ProjectCardFormValues,
  ProjectCardProps,
  projectCardValidation,
  sumTotalExpenses,
  toExpenseNumber,
  TOTAL_EXPENSES_EXCEEDED_ERROR,
  useProjectCardForm
} from '../../components/ProjectCardForm/ProjectCardForm.model';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  EnumerationDto,
  FollowUpsDto,
  ProjectExpenseDto,
  ProjectHistoryDto,
  ProjectRequestDto
} from '../../build/generated-sources/openapi';
import {
  useEnumerationsApi,
  useFollowUpsApi,
  useProjectsApi,
  useUsersApi
} from '../../service/Api.service';
import { AuthContext } from '../../core/auth/AuthContextProvider';
import { useNavigate, useParams } from 'react-router-dom';
import { NotificationType, showNotification } from '../../core/notifications/Notifications';
import { Flex, Group } from '@mantine/core';
import { CustomSelect } from '../../components/CustomInputFields/CustomSelect/CustomSelect';
import { useReactToPrint } from 'react-to-print';
import { CommentsDrawer } from '../../components/CommentsDrawer/CommentsDrawer';
import { useDisclosure } from '@mantine/hooks';
import { CustomButton } from '../../components/CustomButton/CustomButton';
import handleErrors from '../../core/error-handler/ErrorHandler';
import {
  dateToFormattedStringForSave,
  getExpensesYears,
  getSustainabilityTableYears
} from '../../core/util/Util';
import { useDatabaseLock } from '../../core/database-lock/DatabaseLockProvider';
import { HistoryDrawer } from '../../components/HistoryDrawer/HistoryDrawer';

export const ProjectCard = ({ data, handleRefresh }: ProjectCardProps) => {
  const { id } = useParams();
  const authContext = useContext(AuthContext);
  // [DAP-030] Přiřazovat řešitele smí jen editor (superuser/manager/sysadmin) — jen ten vidí select.
  const canAssign = authContext.hasRole(['superuser', 'manager', 'sysadmin']);
  // Seznam uživatelů pro select „Přiřazený řešitel" — načítáme jen pro editory.
  const [usersList, setUsersList] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    if (canAssign) {
      useUsersApi()
        .getUsers()
        .then((result) => {
          setUsersList(
            (result?.data ?? []).map((keycloakUser) => ({
              label: `${keycloakUser.firstName} ${keycloakUser.lastName}`,
              value: keycloakUser.keycloakId ?? ''
            }))
          );
        })
        .catch((error) => handleErrors(error, 'Chyba při načítání uživatelů', false));
    }
  }, []);
  // [DAP-030] Aktuálně přiřazený řešitel (keycloakId) pro select nad kartou; synchronizuje se z dat.
  const [assignedTo, setAssignedTo] = useState<string | undefined>(undefined);
  useEffect(() => {
    setAssignedTo(data?.assignedTo?.keycloakId);
  }, [data]);
  // Přiřazení se ukládá okamžitě přes dedikovaný endpoint (editor-only na BE). Při chybě revert.
  const handleAssign = (value: string | null) => {
    if (!id) {
      return;
    }
    const previous = assignedTo;
    setAssignedTo(value ?? undefined);
    useProjectsApi()
      .assignProject(Number(id), { assignedToKeycloakId: value ?? null })
      .then(() =>
        showNotification('Přiřazení řešitele bylo uloženo', 'assign-project', NotificationType.INFO)
      )
      .catch((error) => {
        setAssignedTo(previous);
        handleErrors(error, 'Chyba při přiřazení řešitele', false);
      });
  };
  const { canModify, isLocked } = useDatabaseLock();
  const [opened, { open, close }] = useDisclosure(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [enumerations, setEnumerations] = useState<EnumerationDto[]>([]);
  const [followUps, setFollowUps] = useState<FollowUpsDto>({});
  const [combinedExpenses, setCombinedExpenses] = useState<Map<number, ProjectExpenseDto[]>>(
    new Map<number, ProjectExpenseDto[]>()
  );
  const [expensesTotal, setExpensesTotal] = useState<ExpensesTotal>();
  const [projectHistory, setProjectHistory] = useState<ProjectHistoryDto[]>([]);
  const [historyOpened, { open: openHistory, close: closeHistory }] = useDisclosure(false);
  const [isPrint, setIsPrint] = useState(false);
  const navigate = useNavigate();

  const componentRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: data?.name,
    onBeforeGetContent: () => {
      setIsPrint(true);
      document.body.style.filter = 'blur(2px)';
    },
    onAfterPrint: () => {
      setIsPrint(false);
      document.body.style.filter = 'none';
    }
  });

  const loadHistory = () => {
    if (!id) return;
    useProjectsApi()
      .getProjectHistory(Number(id))
      .then((res) => setProjectHistory(res.data))
      .catch((err) => handleErrors(err, 'Chyba při načítání historie změn projektu', false));
  };

  useEffect(() => {
    if (id) {
      const yearlyExpensesDataMap = getYearlyExpensesDataMap(data);
      setCombinedExpenses(yearlyExpensesDataMap);
    } else {
      const yearlyExpensesDataMap = getDefaultYearlyExpensesDataMap();
      setCombinedExpenses(yearlyExpensesDataMap);
    }
  }, [data]);

  useEffect(() => {
    useEnumerationsApi()
      .getEnumerations()
      .then((result) => {
        setEnumerations(result.data);
      })
      .catch((error) => {
        handleErrors(error, 'Chyba při načítání číselniků', false);
      });
    useFollowUpsApi()
      .getFollowUps()
      .then((result) => {
        setFollowUps(result.data);
      })
      .catch((error) => {
        handleErrors(error, 'Chyba při načítání dat pro návaznost PR KK a SRR ČR', false);
      });
  }, []);

  useEffect(() => {
    loadHistory();
  }, [id]);

  const form = useProjectCardForm({
    name: 'projectCardDetailForm',
    validateInputOnChange: true,
    initialValues: getProjectCardValues(data),
    validate: projectCardValidation
  });

  // Mantine přečte initialValues jen jednou při vytvoření formuláře. Po uložení se ale
  // z backendu znovu načtou data (handleRefresh -> getProjectById -> nový prop `data`),
  // takže formulář musíme přeseedovat ručně, aby zobrazil hodnoty z backendu (např.
  // zaokrouhlená procenta v rozpadu financování), ne to, co měl uživatel v paměti.
  // Úvodní mount přeskakujeme – ten už pokrývají initialValues.
  const isInitialData = useRef(true);
  useEffect(() => {
    if (isInitialData.current) {
      isInitialData.current = false;
      return;
    }
    if (!id || !data) return;
    // getProjectCardValues generuje náhodné klíče a null data převádí na new Date(),
    // proto hodnotu spočítáme jen jednou a tutéž předáme do setValues i resetDirty,
    // aby formulář nezůstal nesprávně označený jako "dirty".
    const values = getProjectCardValues(data);
    form.setValues(values);
    form.resetDirty(values);
  }, [data]);

  useEffect(() => {
    const implYears =
      form.values.implementationStart && form.values.implementationEnd
        ? getExpensesYears(
            form.values.preprojectPreparationStart,
            form.values.implementationStart,
            form.values.implementationEnd
          )
        : [];
    // [DAP-040] Roky 2. tabulky (od přípravy/realizace do ukončení udržitelnosti) včetně roku mezi
    // koncem realizace a začátkem udržitelnosti — bez něj v mapě by se jeho řádek nevykreslil.
    const sustYears =
      getSustainabilityTableYears(
        form.values.preprojectPreparationStart,
        form.values.implementationStart,
        form.values.sustainabilityPeriodStart,
        form.values.sustainabilityPeriodEnd
      ) ?? [];

    if (implYears.length === 0 && sustYears.length === 0) return;

    // DAP-017: fillMissingExpenseYears doplní nejen zcela nové roky, ale i roky, které
    // v mapě už jsou s prázdným/neúplným polem položek (dřívější guard `prev.has(year)`
    // takový rok přeskočil a jeho buňky pak nešly editovat).
    setCombinedExpenses((prev) => fillMissingExpenseYears(prev, [...implYears, ...sustYears]));
  }, [
    form.values.preprojectPreparationStart,
    form.values.implementationStart,
    form.values.implementationEnd,
    form.values.sustainabilityPeriodStart,
    form.values.sustainabilityPeriodEnd
  ]);

  const createProject = (projectDto: ProjectRequestDto) => {
    if (!canModify) return;
    useProjectsApi()
      .createProject(projectDto)
      .then((result) => {
        showNotification(
          'Projekt byl úspěšně uložen',
          'project_' + projectDto.name,
          NotificationType.INFO
        );
        navigate('/projekty/' + result.data.id);
        setLoading(false);
      })
      .catch((error) => {
        handleErrors(error);
        setLoading(false);
      });
  };

  const updateProject = (projectDto: ProjectRequestDto) => {
    if (!canModify) return;
    useProjectsApi()
      .updateProject(Number(id), projectDto)
      .then((result) => {
        showNotification(
          'Projekt byl úspěšně uložen',
          'project_' + projectDto.name,
          NotificationType.INFO
        );
        loadHistory();
        handleRefresh?.();
        setLoading(false);
      })
      .catch((error) => {
        handleErrors(error);
        setLoading(false);
      });
  };

  const onFormSubmit = (
    values: ReturnType<(values: ProjectCardFormValues) => ProjectCardFormValues>,
    event: React.FormEvent<HTMLFormElement> | undefined
  ) => {
    if (!canModify) return;

    // [DAP-031] Celkové výdaje (uznatelné + neuznatelné) nesmí přesáhnout celkové
    // (předpokládané) náklady projektu. Počítáme z combinedExpenses, protože právě z nich
    // se níže staví ukládané DTO (stav `expensesTotal` je o render pozadu).
    if (
      exceedsTotalEstimatedCosts(sumTotalExpenses(combinedExpenses), values.totalEstimatedCosts)
    ) {
      form.setFieldError('totalEstimatedCosts', TOTAL_EXPENSES_EXCEEDED_ERROR);
      showNotification(
        TOTAL_EXPENSES_EXCEEDED_ERROR,
        'total-expenses-exceeded',
        NotificationType.ERROR
      );
      return;
    }

    setLoading(true);
    const { created, createdBy, updated, updatedBy, ...projectDto } = values as any;
    projectDto.expenses = Array.from(combinedExpenses.values()).flatMap((entry) => entry);
    // [DAP-035/DAP-032] Mantine NumberInput může poslat řetězec ('' po vymazání pole);
    // bez normalizace by na wire šel string a backend by dostal jinou hodnotu, než karta počítá.
    projectDto.ownResourcesIneligibleAmount = toExpenseNumber(values.ownResourcesIneligibleAmount);
    projectDto.contactEmails = values.contactEmails?.map((item) => item.email).join(';');
    projectDto.indicators = values.indicators?.map(({ key, ...rest }) => rest);
    projectDto.preprojectPreparationStart = dateToFormattedStringForSave(
      values.preprojectPreparationStart
    );
    projectDto.implementationStart = dateToFormattedStringForSave(values.implementationStart);
    projectDto.implementationEnd = dateToFormattedStringForSave(values.implementationEnd);
    projectDto.financialEnd = dateToFormattedStringForSave(values.financialEnd);
    projectDto.sustainabilityPeriodStart = dateToFormattedStringForSave(
      values.sustainabilityPeriodStart
    );
    projectDto.sustainabilityPeriodEnd = dateToFormattedStringForSave(
      values.sustainabilityPeriodEnd
    );
    // [DAP-021] legalActDate je nově String DTO (jako ostatní data) → ukládáme shodně:
    // prázdná hodnota jde jako '' a backend ji přes parseLocalDateString převede na null.
    projectDto.legalActDate = dateToFormattedStringForSave(values.legalActDate);
    if (id) {
      updateProject(projectDto);
    } else {
      createProject(projectDto);
    }
  };

  const updateExpensesData = (updatedData: ExpensesTotal) => {
    setExpensesTotal(updatedData);
  };

  const updateCombinedExpenses = (year: number, yearDataCopy: ProjectExpenseDto[]) => {
    setCombinedExpenses((prevCombinedExpenses) => {
      const newCombinedExpenses = new Map(prevCombinedExpenses);
      newCombinedExpenses.set(year, yearDataCopy);
      return newCombinedExpenses;
    });
  };

  return (
    <>
      {id && (
        <>
          <Flex justify="space-between" align="flex-end" gap="sm">
            {/* [DAP-030] „Přiřazený řešitel" mimo kartu, vedle akčních tlačítek; jen pro editory. */}
            <div style={{ minWidth: 280 }}>
              {canAssign && (
                <CustomSelect
                  label="Přiřazený řešitel"
                  searchable
                  clearable
                  data={usersList}
                  disabled={isLocked}
                  inputProps={{ value: assignedTo ?? null }}
                  onChangeCallback={handleAssign}
                />
              )}
            </div>
            <Group>
              <CustomButton onClick={handlePrint} label={'Vytisknout'} />
              <CustomButton onClick={open} label={'Komentáře'} />
              <CustomButton onClick={openHistory} label={'Historie'} />
            </Group>
          </Flex>
          <CommentsDrawer opened={opened} close={close} projectId={Number(id)}></CommentsDrawer>
          <HistoryDrawer opened={historyOpened} close={closeHistory} history={projectHistory} />
        </>
      )}

      <ProjectCardForm
        ref={componentRef}
        form={form}
        isUpdateProject={!!data}
        isLoading={loading}
        combinedExpenses={combinedExpenses}
        updateCombinedExpenses={updateCombinedExpenses}
        updateExpensesData={updateExpensesData}
        onFormSubmit={onFormSubmit}
        enumerations={enumerations}
        followUps={followUps}
        createdBy={data ? data.createdBy : undefined}
        createdByLegacy={data ? data.createdByLegacy : undefined}
        isLocked={isLocked}
        isPrint={isPrint}
      />
    </>
  );
};
